type WeightKey = 'regular' | 'medium' | 'semiBold' | 'bold';
type SizeKey = 'XS' | 'SM' | 'MD' | 'LG' | 'XL' | '2XL';

type Leaf = {
  fontSize: string;
  lineHeight: number | string;
  letterSpacing?: string;
  weights?: Partial<Record<WeightKey, number>>;
};

type KindBucket = Record<SizeKey, Leaf>;

export const typographyKinds: Record<'text' | 'display', KindBucket> = {
  display: {
    '2XL': {
      fontSize: '4.5rem',
      lineHeight: 1.25,
    },
    XL: {
      fontSize: '3.75rem',
      lineHeight: 1.2,
    },
    LG: {
      fontSize: '3rem',
      lineHeight: 1.25,
    },
    MD: {
      fontSize: '2.25rem',
      lineHeight: 1.22,
    },
    SM: {
      fontSize: '1.875rem',
      lineHeight: 1.33,
    },
    XS: {
      fontSize: '1.5rem',
      lineHeight: 1.4,
    },
  },
  text: {
    '2XL': {
      fontSize: '1.5rem',
      lineHeight: 1.5,
    },
    XL: {
      fontSize: '1.25rem',
      lineHeight: 1.56,
    },
    LG: {
      fontSize: '1.125rem',
      lineHeight: 1.5,
    },
    MD: {
      fontSize: '1rem',
      lineHeight: "20px",
    },
    SM: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
    XS: {
      fontSize: '0.75rem',
      lineHeight: 1.5,
    },
  },
};

const defaultWeights: Record<WeightKey, number> = {
  regular: 400,
  medium: 500,
  semiBold: 600,
  bold: 700,
};

export function buildTypography(fontFamily = 'Readex Pro') {
  // flat tokens for direct use: display2XL_bold, textMD_medium, etc.
  const tokens: Record<
    string,
    {
      fontSize: string;
      lineHeight: number | string;
      letterSpacing?: string;
      fontWeight: number;
    }
  > = {};

  (Object.entries(typographyKinds) as ['text' | 'display', KindBucket][]).forEach(
    ([kind, sizes]) => {
      (Object.entries(sizes) as [SizeKey, Leaf][]).forEach(([size, base]) => {
        const weights = { ...defaultWeights, ...(base.weights ?? {}) };
        (Object.entries(weights) as [WeightKey, number][]).forEach(([wKey, wVal]) => {
          // Convert size key format: '2XL' -> '2XL', 'XL' -> 'XL', etc.
          const sizeKey = size;
          // Construct the key in camelCase format: display2XL_bold, textMD_medium, etc.
          const tokenKey = `${kind}${sizeKey}_${wKey}`;

          tokens[tokenKey] = {
            fontSize: base.fontSize,
            lineHeight: base.lineHeight,
            ...(base.letterSpacing && { letterSpacing: base.letterSpacing }),
            fontWeight: wVal,
          };
        });
      });
    },
  );

  return {
    fontFamily,
    kinds: typographyKinds, // structured access: theme.typography.kinds.text.SM
    ...tokens, // flat tokens: theme.typography.textSM_bold
  };
}

// Export a default instance for convenience
export const typography = buildTypography();
